import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d9ng7nm8mbnljin5a070', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore parse errors
      }

      if (response.ok && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.toLowerCase().includes('spam')) {
          setFormStatus('error');
          setFormError(serverMsg);
        } else {
          setFormStatus('error');
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '关于天美影视 - 高清电影大全在线观看平台',
    description: '了解天美影视品牌故事，查看用户口碑评价，联系我们。天美影视电影大全，最新电影高清免费在线观看。',
    url: 'https://www.tianmeiying.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '天美影视',
      url: 'https://www.tianmeiying.com',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.tianmeiying.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  return (
    <>
      <SchemaOrg data={webPageSchema} />
      <SchemaOrg data={breadcrumbSchema} />
      <Navbar />
      <main>
        <article className="bg-background-200 min-h-screen">
          <div className="w-full px-4 md:px-6 pt-24 pb-10">
            <div className="max-w-[1000px] mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />

              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 mb-3 animate-fade-in-up animation-fill-both">
                关于天美影视
              </h1>
              <p className="text-foreground-500 text-sm mb-10 animate-fade-in-up animation-fill-both animation-delay-100">
                天美影视电影大全，致力于为用户提供最新最全的高清电影在线观看服务
              </p>

              <section className="mb-12 animate-fade-in-up animation-fill-both animation-delay-200">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-100 mb-4">
                  品牌介绍
                </h2>
                <div className="rounded-xl bg-background-700/40 border border-background-700/40 p-6 md:p-8">
                  <p className="text-sm md:text-base text-foreground-400 leading-relaxed mb-4">
                    天美影视成立于2020年，是一个专注于免费高清电影在线观看的综合影视平台。我们致力于为全球华语用户提供最新、最全、最流畅的电影观看体验。
                  </p>
                  <p className="text-sm md:text-base text-foreground-400 leading-relaxed mb-4">
                    平台汇聚了超过200部影视资源，涵盖<strong>动作片</strong>、<strong>喜剧片</strong>、<strong>爱情片</strong>、<strong>科幻片</strong>、<strong>恐怖片</strong>等多种类型，每日更新最新电影资源，确保用户能够第一时间观看到最新上映的热门大片。
                  </p>
                  <p className="text-sm md:text-base text-foreground-400 leading-relaxed">
                    天美影视采用先进的全球CDN加速技术，确保高清画质流畅播放不缓冲。无论您使用电脑、平板还是手机，都能享受到极致的在线观影体验。我们的使命是让每一个人都能免费享受高品质的电影娱乐。
                  </p>
                </div>
              </section>

              <section className="mb-12 animate-fade-in-up animation-fill-both animation-delay-300">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-100 mb-4">
                  用户口碑
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    {
                      name: '张先生',
                      role: '资深影迷',
                      avatar: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20male%20portrait%20avatar%2C%20simple%20light%20background%2C%20warm%20lighting%2C%20clean%20professional%20look&width=100&height=100&seq=avatar-01&orientation=squarish',
                      quote: '天美影视真的太好用了！片源多更新快，画质清晰不卡顿，每天晚上都要刷一部电影才睡觉。',
                    },
                    {
                      name: '李女士',
                      role: '上班族',
                      avatar: 'https://readdy.ai/api/search-image?query=Professional%20Chinese%20female%20portrait%20avatar%2C%20simple%20light%20background%2C%20warm%20lighting%2C%20clean%20professional%20look&width=100&height=100&seq=avatar-02&orientation=squarish',
                      quote: '下班后最喜欢来天美影视看电影放松，不用注册不用付费，想怎么看就怎么看，推荐给所有朋友了！',
                    },
                    {
                      name: '王同学',
                      role: '大学生',
                      avatar: 'https://readdy.ai/api/search-image?query=Young%20Chinese%20male%20student%20portrait%20avatar%2C%20simple%20light%20background%2C%20warm%20lighting%2C%20clean%20casual%20look&width=100&height=100&seq=avatar-03&orientation=squarish',
                      quote: '室友推荐的网站，用了快一年了。分类清晰找片方便，新片上线也很快，学生党必备！',
                    },
                  ].map((review, i) => (
                    <div
                      key={i}
                      className="rounded-xl bg-background-700/40 border border-background-700/40 p-5 flex flex-col gap-3 hover:border-primary-500/20 transition-all duration-300"
                    >
                      <div className="flex items-center gap-3">
                        <img
                          src={review.avatar}
                          alt={review.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <div>
                          <p className="text-sm font-medium text-foreground-200">{review.name}</p>
                          <p className="text-xs text-foreground-600">{review.role}</p>
                        </div>
                      </div>
                      <p className="text-sm text-foreground-400 leading-relaxed italic">
                        "{review.quote}"
                      </p>
                      <div className="flex items-center gap-0.5 text-primary-500 text-xs">
                        {[...Array(5)].map((_, j) => (
                          <i key={j} className="ri-star-fill"></i>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="animate-fade-in-up animation-fill-both animation-delay-400">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-100 mb-4">
                  联系我们
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <div className="rounded-xl bg-background-700/40 border border-background-700/40 p-6 space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 flex-shrink-0">
                          <i className="ri-mail-line text-lg text-primary-400"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-200">电子邮箱</p>
                          <p className="text-sm text-foreground-500">contact@tianmeiying.com</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 flex-shrink-0">
                          <i className="ri-phone-line text-lg text-primary-400"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-200">客服电话</p>
                          <p className="text-sm text-foreground-500">400-888-8888</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 flex-shrink-0">
                          <i className="ri-map-pin-line text-lg text-primary-400"></i>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground-200">公司地址</p>
                          <p className="text-sm text-foreground-500">北京市朝阳区光华路88号天美影视大厦</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <form
                      data-readdy-form=""
                      id="contact-form"
                      onSubmit={handleSubmit}
                      className="rounded-xl bg-background-700/40 border border-background-700/40 p-6 space-y-4"
                    >
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-300 mb-1.5">
                          您的姓名
                        </label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          placeholder="请输入您的姓名"
                          className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-600/50 border border-background-600/60 text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-300 mb-1.5">
                          电子邮箱
                        </label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          placeholder="请输入您的邮箱"
                          className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-600/50 border border-background-600/60 text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-300 mb-1.5">
                          留言内容
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          rows={4}
                          required
                          maxLength={500}
                          placeholder="请输入您想说的话（最多500字）"
                          className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-600/50 border border-background-600/60 text-foreground-200 placeholder:text-foreground-600 focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
                        ></textarea>
                        <p className="text-xs text-foreground-600 mt-1">最多500字符</p>
                      </div>

                      <input
                        type="text"
                        name="phone_alt"
                        className="visually-hidden-field"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />

                      {formStatus === 'success' && (
                        <div className="flex items-center gap-2 p-3 rounded-lg bg-primary-500/10 border border-primary-500/20">
                          <i className="ri-checkbox-circle-line text-primary-400"></i>
                          <p className="text-sm text-primary-400">感谢您的留言，我们会尽快回复！</p>
                        </div>
                      )}
                      {formStatus === 'error' && (
                        <div className="flex items-start gap-2 p-3 rounded-lg bg-accent-500/10 border border-accent-500/20">
                          <i className="ri-error-warning-line text-accent-400 mt-0.5"></i>
                          <p className="text-sm text-accent-400">{formError}</p>
                        </div>
                      )}

                      <button
                        type="submit"
                        className="w-full px-5 py-2.5 bg-primary-500 text-background-950 font-semibold rounded-lg text-sm hover:bg-primary-400 transition-all duration-300 whitespace-nowrap cursor-pointer"
                      >
                        提交留言
                      </button>
                    </form>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}